import React, {
    useEffect,
    useRef,
    forwardRef,
    useImperativeHandle,
    useState,
} from "react";
import { useSelector } from "react-redux";
import Message from "../Message";
import MessageLoader from "../MessageLoader";
import LinkCard from "../LinkCard";
import NewsSourceCards from "../NewsSourceCards";
import HarrisChainOfThought from "../HarrisChainOfThought";
import {
    selectMessages,
    selectIsAssistantResponding,
    selectIsTyping,
    selectErrorMessage,
    selectTaskStatusMessage,
    selectIsChatOpen,
} from "../../Selectors/AssistantSelector";
import AssistantThinkingLoader from "../AssistantThinkingLoader";
import NewsArticlesAccordian from "../NewsArticlesAccordian";
import SourcesPill from "../SourcesPill";
import "./MessageContent.scss";
import "../Message/Message.scss";
import Welcome from "../Welcome";
import ErrorBanner from '../ErrorBanner'
import IntroWelcomeMessage from "../IntroWelcomeMessage";
import { AI_ASSISTANT_CONSTANTS } from "../../constants/aiAssistantConstants";

const MessageContent = forwardRef((props, ref) => {
    const messages = useSelector(selectMessages);
    const isUserTyping = useSelector(selectIsTyping);
    const isAssistantResponding = useSelector(selectIsAssistantResponding);
    const errorMessage = useSelector(selectErrorMessage);
    const taskStatusMessage = useSelector(selectTaskStatusMessage);
    const isChatOpen = useSelector(selectIsChatOpen);
    const userId = useSelector((state) => state.userDetail?.id ?? null);
    const [statusTrail, setStatusTrail] = useState([]);
    const [isTaskStatusClosed, setIsTaskStatusClosed] = useState(false);
    const [hasShownIntroThisSession, setHasShownIntroThisSession] = useState(false);
    const [hasInteractedThisSession, setHasInteractedThisSession] = useState(false);

    const messageEndRef = useRef(null);
    const previousTypes = [];

    const getPreviousMsgType = (msgType, index) => {
        previousTypes.push(msgType);
        return index === 0 ? "" : previousTypes[index - 1];
    };

    const scrollToBottom = () => {
        const container = messageEndRef.current;
        if (container) {
            container.scrollTop = container.scrollHeight;
            handleScroll();
        }
    };

    useImperativeHandle(ref, () => ({
        scrollToBottom,
    }));

    useEffect(() => {
        const timer = setTimeout(() => {
            scrollToBottom();
        }, 100);
        return () => clearTimeout(timer);
    }, [messages, isAssistantResponding, errorMessage]);

    useEffect(() => {
        if (!statusTrail.length) return;
        const timer = setTimeout(() => {
            scrollToBottom();
        }, 100);
        return () => clearTimeout(timer);
    }, [statusTrail]);

    const normalizeStatusEntry = (status) => {
        const phase = status?.taskTitle || status?.stage || status?.message || "Working on it...";
        const detail = status?.taskMessage || status?.message || "";
        return {
            phase,
            detail,
            state: status?.type === "error" ? "error" : "active",
            timestamp: Date.now(),
        };
    };

    useEffect(() => {
        if (!taskStatusMessage) return;
        if (taskStatusMessage?.type === "task_status_end") {
            setStatusTrail([]);
            setIsTaskStatusClosed(true);
            return;
        }
        setIsTaskStatusClosed(false);
        setStatusTrail((prev) => {
            const nextStatus = normalizeStatusEntry(taskStatusMessage);
            const last = prev[prev.length - 1];
            const isDuplicate =
                last &&
                last.phase === nextStatus.phase &&
                last.detail === nextStatus.detail;
            if (isDuplicate) {
                return prev;
            }
            const next = [...prev, nextStatus];
            const MAX_STATUSES = 6;
            return next.slice(-MAX_STATUSES);
        });
    }, [taskStatusMessage]);

    useEffect(() => {
        if (isAssistantResponding) {
            setIsTaskStatusClosed(false);
            return;
        }
        setStatusTrail([]);
        setIsTaskStatusClosed(false);
    }, [isAssistantResponding]);

    const handleScroll = () => {
        if (!messageEndRef.current) return;
        const { scrollTop, scrollHeight, clientHeight } = messageEndRef.current;
        // Check if we are not at the bottom (allow a small threshold)
        const isNotAtBottom = scrollHeight - scrollTop - clientHeight > 10;
        
        // Dispatch an event so the parent can toggle the shadow class
        const event = new CustomEvent("chat-scroll-state", { 
            detail: { isScrolled: isNotAtBottom && messages.length > 0 } 
        });
        window.dispatchEvent(event);
    };

    useEffect(() => {
        const container = messageEndRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
            // Initial check
            handleScroll();
            return () => container.removeEventListener('scroll', handleScroll);
        }
    }, [messages.length]);

    useEffect(() => {
        if (messages.length === 0) {
            setStatusTrail([]);
            setIsTaskStatusClosed(true);
            return;
        }
        // As soon as there is any message in this tab, we consider the user to have interacted.
        if (!hasInteractedThisSession) {
            setHasInteractedThisSession(true);
        }
    }, [messages.length, hasInteractedThisSession]);

    const statusSteps = statusTrail.map((status, index) => ({
        title: status.phase,
        items: status.detail ? [status.detail] : [],
        state:
            status.state === "error"
                ? "error"
                : index === statusTrail.length - 1
                    ? "active"
                    : "done",
    }));
    const showChainOfThought =
        isAssistantResponding && !isTaskStatusClosed && statusTrail.length > 0;

    // Show the inline thinking indicator when the assistant is responding
    // but no streaming text has appeared yet in the message list.
    const lastMsg = messages[messages.length - 1];
    const hasStreamingText =
        lastMsg &&
        (lastMsg.type || "").toLowerCase() === "harris" &&
        (lastMsg.message_type || "text") === "text" &&
        (lastMsg.payload?.text || lastMsg.text || "").length > 0;
    const showThinkingIndicator =
        isAssistantResponding && !hasStreamingText && !showChainOfThought;

    const introSeenKey = (() => {
        if (typeof window === "undefined") return null;
        if (userId == null) return null;
        return `${AI_ASSISTANT_CONSTANTS.STORAGE_KEYS.AI_ASSISTANT_INTRO_SEEN_PREFIX}${userId}`;
    })();
    const hasSeenIntroFromStorage = (() => {
        if (!introSeenKey) return false;
        try {
            return window.localStorage.getItem(introSeenKey) === "1";
        } catch {
            return false;
        }
    })();
    const shouldShowIntroWelcome =
        isChatOpen &&
        messages.length === 0 &&
        Boolean(introSeenKey) &&
        !hasInteractedThisSession &&
        (!hasSeenIntroFromStorage || hasShownIntroThisSession);

    useEffect(() => {
        if (!introSeenKey) return;
        if (!shouldShowIntroWelcome) return;
        if (hasShownIntroThisSession) return;

        setHasShownIntroThisSession(true);

        if (!hasSeenIntroFromStorage) {
            try {
                window.localStorage.setItem(introSeenKey, "1");
            } catch {
                // Ignore storage failures (private mode, quota, etc.)
            }
        }
    }, [shouldShowIntroWelcome, introSeenKey, hasSeenIntroFromStorage, hasShownIntroThisSession]);

    return (
        <div className="card-content">
            {messages.length === 0 ? (
                shouldShowIntroWelcome ? (
                    <ul
                        className="msg-container"
                        ref={messageEndRef}
                        role="log"
                        aria-live="polite"
                        aria-label="Chat messages"
                    >
                        <IntroWelcomeMessage />
                    </ul>
                ) : (
                    <Welcome />
                )
            ) : (
                <ul className="msg-container" ref={messageEndRef} role="log" aria-live="polite" aria-label="Chat messages">
                    {messages.map((msg, index) => {
                        const previousType = getPreviousMsgType(msg.type, index);
                        const messageType = msg.message_type || "text";
                        const payload = { ...(msg.payload || {}), ...msg };
                        if (messageType === "link") {
                            const link = payload.link ?? "";
                            const title = payload.title ?? "Link";
                            const description = payload.description ?? payload.text ?? "";
                            return (
                                <li
                                    key={index}
                                    className="msg-text align-left chat-item chat-item--link show"
                                >
                                    <div className="chat-block">
                                        <LinkCard
                                            title={title}
                                            description={description}
                                            link={link}
                                        />
                                    </div>
                                </li>
                            );
                        }
                        if (messageType === "sources") {
                            const articles = Array.isArray(payload.sources) ? payload.sources : [];
                            const grounding = Array.isArray(payload.grounding_sources) ? payload.grounding_sources : [];
                            if (articles.length === 0 && grounding.length === 0) return null;
                            return (
                                <li
                                    key={index}
                                    className="msg-text align-left chat-item chat-item--sources show"
                                >
                                    <div className="chat-block">
                                        <NewsSourceCards
                                            articles={articles}
                                            source_type={payload.source_type}
                                            grounding_sources={grounding}
                                        />
                                    </div>
                                </li>
                            );
                        }
    // Replace the standalone query_params item to avoid double rendering
                        if (messageType === "query_params") {
                            return null;
                        }
                        return (
                            <Message
                                key={index}
                                msg={msg}
                                previousType={previousType}
                                scrollToBottom={scrollToBottom}
                                messageIndex={index}
                            />
                        );
                    })}
                    {showThinkingIndicator && (
                        <li className="msg-text align-left show thinking-indicator-row">
                            <AssistantThinkingLoader />
                        </li>
                    )}
                    {isUserTyping && <MessageLoader type="You" />}
                    {errorMessage && <ErrorBanner message={errorMessage} />}
                </ul>
            )}
            {showChainOfThought && (
                <div className="cot-wrapper">
                    <HarrisChainOfThought
                        steps={statusSteps}
                        startedAt={statusTrail[0]?.timestamp}
                    />
                </div>
            )}
        </div>
    );

});

export default MessageContent;
